<template>
  <div class="job-filter" ref="jobFilterRoot" :class="{ 'is-open': isPopupOpen }">
    <div class="section-container">
      <div class="section-layout">
        <div class="job-filter-wrapper">
          <JobFilterPopup
            ref="popup"
            id="job-filter-popup"
            :filters="filters"
            :filter-items="filterJobItems"
            :active-filters="activeFilters"
            :dictionary="dictionary"
            @confirm="applyFilters"
            @close="closePopup"
          />
          <button
            v-show="hasFilters"
            class="job-filter-toggle toggle-pill small button-unstyled"
            :aria-label="dictionary.filterToggleLabelDesktop"
            aria-controls="job-filter-popup"
            :aria-expanded="isPopupOpen.toString()"
            data-dm="job-filter.popup-open"
            @click="openPopup"
          >
            <Icon icon="funnel" size="small" color="none">
              <IconFunnel :circle="hasActiveFilters" />
            </Icon>
            <span class="display-m-none text-caption-caps">{{ dictionary.filterToggleLabelMobile }}</span>
            <span class="display-m-inline text-caption-caps">{{ dictionary.filterToggleLabelDesktop }}</span>
            <span class="job-filter-toggle-dot" ref="popupToggleDot"></span>
          </button>
        </div>
      </div>
    </div>
  </div>
  <slot
    :should-render-job="shouldRenderJob"
    :available-job-openings-label="availableJobOpeningsLabel"
  />
</template>

<script setup>
import {
  computed, onMounted, ref, watch,
} from 'vue';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import debounce from 'lodash.debounce';
import JobFilterPopup from '@/components/filter/JobFilterPopup.vue';
import Icon from '@/components/shared/IconBase.vue';
import IconFunnel from '@/components/icons/IconFunnel.vue';
import { isDesktop } from '@/utils/breakpoint';
import { getUrlSearchParams, updateUrlSearchParams } from '@/utils/url';

const props = defineProps({
  filters: {
    type: Object,
    default: () => ({}),
  },
  items: {
    type: Array,
    default: () => [],
  },
  dictionary: {
    type: Object,
    default: () => ({}),
  },
});

// template refs
const jobFilterRoot = ref(null);
const popup = ref(null);
const popupToggleDot = ref(null);

// refs
const isPopupOpen = ref(false);
const activeFilters = ref({});

const hasFilters = computed(() => Object.values(props.filters).some((list) => list?.length));
const hasActiveFilters = computed(() => Boolean(Object.keys(activeFilters.value).length));

watch(isPopupOpen, (newValue) => {
  if (isDesktop()) return;

  if (newValue) {
    disableBodyScroll(jobFilterRoot.value);
  } else {
    enableBodyScroll(jobFilterRoot.value);
  }
});

onMounted(() => {
  // preselected filters through URL query
  activeFilters.value = getPreselectedFiltersFromUrl();

  // dot animation
  updateDotAnimationValues();
  window.addEventListener('resize', debounce(updateDotAnimationValues, 250), { passive: true });
});

watch(activeFilters, (newActiveFilters) => {
  updateUrlWithFilters(newActiveFilters);
});

const activeJobIds = computed(() => filterJobItems(activeFilters.value).map((job) => job.CmsId));
const availableJobOpeningsLabel = computed(() => props.dictionary.availableJobOpeningsLabel?.replace('{numberOfResults}', activeJobIds.value.length));

function updateDotAnimationValues() {
  // set popup height
  const { offsetHeight } = popup.value.root;
  jobFilterRoot.value.style.setProperty('--jf-popup-desktop-calculated-height', `${offsetHeight}px`);

  const popupCloseRect = popup.value.closeButton.getBoundingClientRect();
  const popupToggleDotRect = popupToggleDot.value.getBoundingClientRect();

  // calculate popup dot transformation
  const dotScaleDifference = popupCloseRect.width - popupToggleDotRect.width;
  const topTranslate = ((popupCloseRect.top) - (popupToggleDotRect.top)) + dotScaleDifference / 2;
  const leftTranslate = ((popupCloseRect.left) - popupToggleDotRect.left) + dotScaleDifference;

  const translateStyle = `translate(${leftTranslate}px, ${topTranslate}px)`;
  jobFilterRoot.value.style.setProperty('--jf-popup-dot-translate', translateStyle);
}

function getPreselectedFiltersFromUrl() {
  return Array.from(getUrlSearchParams())
    .reduce((result, [queryParam, queryValue]) => {
      const filters = props.filters[queryParam];
      if (filters) {
        result[queryParam] = queryValue.split(',').filter((v) => filters.includes(v));
      }
      return result;
    }, {});
}

function updateUrlWithFilters(newActiveFilters) {
  const params = Object.keys(props.filters)
    .reduce((result, key) => ({
      ...result,
      [key]: newActiveFilters[key] ? newActiveFilters[key].join(',') : null,
    }), {});
  updateUrlSearchParams(params);
}

function shouldRenderJob(id) {
  return activeJobIds.value.includes(id);
}

function filterJobItems(filters) {
  return props.items.filter((job) => isJobActive(job, filters));
}

function isJobActive(job, filters) {
  const { Cities, Departments, Experiences } = filters;
  // if no active filters are selected for a group then return all results from that group
  return (!Cities?.length || Cities.some((city) => job.Cities?.includes(city)))
      && (!Departments?.length || Departments.some((department) => job.Departments?.includes(department)))
      && (!Experiences?.length || Experiences.some((experience) => job.Experiences?.includes(experience)));
}

function applyFilters(filters) {
  activeFilters.value = filters;
}

function openPopup() {
  isPopupOpen.value = true;
}

function closePopup() {
  isPopupOpen.value = false;
}
</script>
